import React, { useState, useRef, useEffect, useCallback } from "react";
import Fuse from "fuse.js";
import HotelIcon from "@mui/icons-material/Hotel";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { InputAdornment, TextField } from "@mui/material";
import axios from "axios";

let cachedSuggestions: string[] | null = null;

async function fetchSuggestions(): Promise<string[]> {
  if (cachedSuggestions) return cachedSuggestions;
  try {
    const res = await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/hosts/public/locations/`);
    const { cities = [], localities = [] } = res.data;
    cachedSuggestions = Array.from(new Set([...cities, ...localities]));
    return cachedSuggestions;
  } catch {
    return [];
  }
}

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  /** When provided, renders a plain <input> with this className instead of MUI TextField */
  inputClassName?: string;
}

const CityAutocomplete: React.FC<CityAutocompleteProps> = ({
  value,
  onChange,
  className,
  placeholder = "E.g. Koramangala, HSR",
  inputClassName,
}) => {
  const [allSuggestions, setAllSuggestions] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fuseRef = useRef<Fuse<string> | null>(null);

  useEffect(() => {
    fetchSuggestions().then((data) => {
      setAllSuggestions(data);
      fuseRef.current = new Fuse(data, {
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: 2,
        includeScore: true,
      });
    });
  }, []);

  const getSuggestions = useCallback((input: string) => {
    if (!input || input.length < 2) return [];
    const lower = input.toLowerCase();
    const startsWith = allSuggestions.filter((s) => s.toLowerCase().startsWith(lower));
    const contains = allSuggestions.filter((s) => !s.toLowerCase().startsWith(lower) && s.toLowerCase().includes(lower));
    const exactSet = new Set([...startsWith, ...contains].map((s) => s.toLowerCase()));
    const fuzzy = fuseRef.current
      ? fuseRef.current.search(input).map((r) => r.item).filter((s) => !exactSet.has(s.toLowerCase()))
      : [];
    return [...startsWith, ...contains, ...fuzzy].slice(0, 6);
  }, [allSuggestions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    const s = getSuggestions(val);
    setSuggestions(s);
    setOpen(s.length > 0);
    setActiveIndex(-1);
  };

  const handleSelect = (city: string) => {
    onChange(city);
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const dropdown = open && suggestions.length > 0 && (
    <ul
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        zIndex: 1300,
        margin: 0,
        padding: "4px 0",
        listStyle: "none",
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        maxHeight: "240px",
        overflowY: "auto",
      }}
    >
      {suggestions.map((city, idx) => (
        <li
          key={city}
          onMouseDown={() => handleSelect(city)}
          onMouseEnter={() => setActiveIndex(idx)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 16px",
            cursor: "pointer",
            fontSize: "14px",
            background: idx === activeIndex ? "#f5f5f5" : "transparent",
            transition: "background 0.1s",
          }}
        >
          <LocationOnIcon style={{ fontSize: 16, color: "#888", flexShrink: 0 }} />
          {city}
        </li>
      ))}
    </ul>
  );

  return (
    <div ref={wrapperRef} style={{ position: "relative" }} className={className}>
      {inputClassName ? (
        <input
          className={inputClassName}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            const s = getSuggestions(value);
            if (s.length > 0) { setSuggestions(s); setOpen(true); }
          }}
        />
      ) : (
        <TextField
          variant="outlined"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            const s = getSuggestions(value);
            if (s.length > 0) { setSuggestions(s); setOpen(true); }
          }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HotelIcon />
              </InputAdornment>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
        />
      )}
      {dropdown}
    </div>
  );
};

export default CityAutocomplete;
